import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

interface InquiryRecord {
    _id: string;
    name: string;
    email: string;
    mobile: string;
    status: string;
    createdAt: string;
    timeline: {
        status: string;
        notes: string;
        date: string;
    }[];
}

export default function AdminPackageInquiries() {
    const { token } = useAuth();
    const [inquiries, setInquiries] = useState<InquiryRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [itemToDelete, setItemToDelete] = useState<string | null>(null);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

    useEffect(() => {
        if (token) loadData();
    }, [token]);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await api.adminGetPackageInquiries(token!);
            if (Array.isArray(data)) setInquiries(data);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load inquiries.");
        } finally {
            setLoading(false);
        }
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        try {
            await api.adminDeletePackageInquiry(token!, itemToDelete);
            toast.success("Inquiry deleted successfully");
            loadData();
        } catch (error) {
            console.error(error);
            toast.error("Failed to delete inquiry");
        } finally {
            setItemToDelete(null);
        }
    };

    const confirmBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        try {
            await api.adminBulkDeletePackageInquiries(token!, selectedIds);
            toast.success(`${selectedIds.length} inquiries deleted successfully`);
            setSelectedIds([]);
            loadData();
        } catch (error) {
            console.error(error);
            toast.error("Failed to bulk delete inquiries");
        } finally {
            setShowBulkDeleteModal(false);
        }
    };

    const filtered = inquiries.filter(i => 
        (i.name || '').includes(search) ||
        (i.email || '').includes(search) ||
        (i.mobile || '').includes(search)
    );

    return (
        <AdminLayout>
            <div className="animate__animated animate__fadeIn">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                    <div>
                        <h3 className="text-3xl md:text-4xl font-bold text-black mb-2 leading-none">Package Inquiries</h3>
                        <p className="text-[14px] md:text-[15px] font-bold text-black leading-relaxed">Monitor and fulfill subscription requests from platform members.</p>
                    </div>
                    <div className="hidden lg:flex items-center gap-4 px-6 py-3 bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-sm">
                        <div className="w-10 h-10 bg-white border-2 border-[#05144b] rounded-md flex items-center justify-center text-[#05144b] shadow-xl shadow-blue-100">
                            <i className="fa-solid fa-gem text-base"></i>
                        </div>
                        <span className="text-[13px] font-bold text-black">{inquiries.length} Total requests active</span>
                    </div>
                </div>

                {/* Main Table Container */}
                <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-sm overflow-hidden">
                    {/* Table Header / Toolbar */}
                    <div className="p-6 md:p-8 border-b border-blue-900/10 dark:border-slate-800 flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-white/50 dark:bg-slate-800/30">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white border-2 border-[#05144b] rounded-md flex items-center justify-center text-[#05144b] shadow-xl shadow-blue-100">
                                <i className="fa-solid fa-envelope-open-text text-lg"></i>
                            </div>
                            <div>
                                <h5 className="text-xl font-bold text-black">Incoming Requests</h5>
                                <p className="text-[12px] font-bold text-black/60 mt-0.5">{filtered.length} matching inquiries</p>
                            </div>
                            {selectedIds.length > 0 && (
                                <button
                                    onClick={() => setShowBulkDeleteModal(true)}
                                    className="ml-4 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 text-white font-bold text-sm hover:bg-rose-700 transition-all shadow-md active:scale-95"
                                    title="Delete Selected"
                                >
                                    <i className="fa-solid fa-trash"></i>
                                    Delete ({selectedIds.length})
                                </button>
                            )}
                        </div>

                        <div className="relative group max-w-lg w-full">
                            <i className="fa-solid fa-magnifying-glass absolute left-5 top-1/2 -translate-y-1/2 text-black/30 group-focus-within:text-black transition-colors"></i>
                             <input
                                type="text"
                                className="block w-full pl-14 pr-6 py-3.5 bg-white border border-gray-100 rounded-md text-[15px] font-bold focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] transition-all text-black placeholder:text-gray-300 shadow-sm"
                                placeholder="Find by name, email or mobile..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    {loading ? (
                         <div className="flex flex-col items-center justify-center py-32">
                            <div className="w-10 h-10 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mb-6"></div>
                            <p className="text-[#05144b] font-bold text-[14px]">Accessing database...</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-32 px-6 text-center">
                            <div className="w-18 h-18 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center mb-6 border-2 border-blue-900/10 dark:border-slate-700 shadow-inner">
                                <i className="fa-solid fa-box-open text-2xl text-blue-950/10"></i>
                            </div>
                            <h5 className="text-xl font-bold text-black mb-2 tracking-tight">No Requests Found</h5>
                            <p className="text-black/60 text-[13px] font-bold">There are no active package inquiries at this time.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto scrollbar-hide">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-white/30">
                                        <th className="px-6 py-5 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100 w-12 text-center">
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 rounded border-slate-300 text-[#05144b] focus:ring-[#05144b] cursor-pointer"
                                                checked={filtered.length > 0 && selectedIds.length === filtered.length}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedIds(filtered.map(i => i._id));
                                                    } else {
                                                        setSelectedIds([]);
                                                    }
                                                }}
                                            />
                                        </th>
                                        <th className="px-8 py-5 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Applicant Status</th>
                                        <th className="px-6 py-5 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Inquiry Context</th>
                                        <th className="px-6 py-5 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Communication</th>
                                        <th className="px-6 py-5 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Received Point</th>
                                        <th className="px-8 py-5 text-right text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Fulfillment</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100/50 dark:divide-slate-800/50">
                                    {filtered.map(i => (
                                        <tr key={i._id} className={`hover:bg-white/80 dark:hover:bg-slate-800/30 transition-all group ${selectedIds.includes(i._id) ? 'bg-blue-50/50 dark:bg-slate-800/80' : ''}`}>
                                            <td className="px-6 py-6 text-center">
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4 rounded border-slate-300 text-[#05144b] focus:ring-[#05144b] cursor-pointer"
                                                    checked={selectedIds.includes(i._id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedIds(prev => [...prev, i._id]);
                                                        } else {
                                                            setSelectedIds(prev => prev.filter(id => id !== i._id));
                                                        }
                                                    }}
                                                />
                                            </td>
                                            <td className="px-8 py-6 whitespace-nowrap">
                                                <div className="flex items-center gap-4">
                                                     <div className="w-11 h-11 rounded-md bg-white border-2 border-[#05144b] flex items-center justify-center text-[#05144b] font-bold text-lg shadow-xl shadow-blue-100 group-hover:scale-105 transition-transform duration-300">
                                                        {i.name?.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="text-[15px] md:text-[16px] font-bold text-black">{i.name}</div>
                                                        <div className="text-[11px] font-bold text-black/40 mt-0.5 leading-none">ID: {i._id.substring(0, 8)}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-6">
                                                 <div className="max-w-xs border-l-2 border-[#05144b] pl-3">
                                                    <p className="text-[13px] font-bold text-black line-clamp-2">
                                                        "{i.timeline?.[i.timeline.length - 1]?.notes || 'No notes provided.'}"
                                                    </p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-6 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <span className="text-[13px] font-bold text-black">{i.email}</span>
                                                    <span className="text-[11px] font-bold text-black/60 mt-0.5">{i.mobile}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-6 whitespace-nowrap">
                                                <span className="text-[13px] font-bold text-black tabular-nums">
                                                    {new Date(i.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </span>
                                            </td>
                                            <td className="px-8 py-6 whitespace-nowrap text-right">
                                                 <div className="flex items-center justify-end gap-3">
                                                     <Link 
                                                        to="/admin-users" 
                                                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-[#05144b] text-[#05144b] rounded-md text-[12px] font-bold transition-all hover:bg-blue-50 shadow-md active:scale-95"
                                                    >
                                                        Assign Package <i className="fa-solid fa-arrow-right-long"></i>
                                                    </Link>
                                                    <button
                                                        onClick={() => setItemToDelete(i._id)}
                                                        className="w-10 h-10 rounded-md bg-white border border-rose-200 hover:bg-rose-50 text-rose-600 font-bold text-[14px] transition-all shadow-sm flex items-center justify-center"
                                                        title="Delete Inquiry"
                                                    >
                                                        <i className="fa-solid fa-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {itemToDelete && (
                <div className="fixed inset-0 z-[3000] flex items-center justify-center px-4">
                <div
                    className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                    onClick={() => setItemToDelete(null)}
                />
                <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden z-10 animate__animated animate__zoomIn animate__faster">
                    <div className="p-6 text-center">
                    <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4 text-rose-600 text-2xl shadow-sm">
                        <i className="fa-solid fa-triangle-exclamation"></i>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Delete Inquiry?</h3>
                    <p className="text-sm text-slate-500 font-medium">This action cannot be undone. Are you sure you want to permanently delete this package inquiry?</p>
                    </div>
                    <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-3">
                    <button
                        onClick={() => setItemToDelete(null)}
                        className="flex-1 py-2.5 rounded-xl bg-white border border-slate-300 text-slate-700 font-bold text-sm hover:bg-slate-50 transition-all shadow-sm active:scale-95"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={confirmDelete}
                        className="flex-1 py-2.5 rounded-xl bg-rose-600 text-white font-bold text-sm hover:bg-rose-700 transition-all shadow-sm active:scale-95"
                    >
                        Delete
                    </button>
                    </div>
                </div>
                </div>
            )}
            {/* Bulk Delete Confirmation Modal */}
            {showBulkDeleteModal && (
                <div className="fixed inset-0 z-[3000] flex items-center justify-center px-4">
                <div
                    className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                    onClick={() => setShowBulkDeleteModal(false)}
                />
                <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden z-10 animate__animated animate__zoomIn animate__faster">
                    <div className="p-6 text-center">
                    <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4 text-rose-600 text-2xl shadow-sm">
                        <i className="fa-solid fa-trash-can"></i>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Delete {selectedIds.length} Inquiries?</h3>
                    <p className="text-sm text-slate-500 font-medium">This action cannot be undone. Are you sure you want to permanently delete {selectedIds.length} package inquiries?</p>
                    </div>
                    <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-3">
                    <button
                        onClick={() => setShowBulkDeleteModal(false)}
                        className="flex-1 py-2.5 rounded-xl bg-white border border-slate-300 text-slate-700 font-bold text-sm hover:bg-slate-50 transition-all shadow-sm active:scale-95"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={confirmBulkDelete}
                        className="flex-1 py-2.5 rounded-xl bg-rose-600 text-white font-bold text-sm hover:bg-rose-700 transition-all shadow-sm active:scale-95"
                    >
                        Delete Selected
                    </button>
                    </div>
                </div>
                </div>
            )}
        </AdminLayout>
    );
}


